<template>
  <div class="flex-grow-1 d-flex flex-column overflow-hidden mt-n2">
    <!-- 搜索条件卡片 -->
    <v-card class="rounded-xl card-bg card-bd flex-shrink-0 px-6 py-5" variant="elevated" elevation="0">
      <div class="d-flex align-center flex-wrap ga-6">
        <div class="search-item">
          <span class="search-label">菜单名称</span>
          <v-text-field v-model="queryParams.menuName" placeholder="请输入菜单名称" variant="outlined" density="compact"
            hide-details rounded="xl" class="search-input" @keyup.enter="handleQuery" />
        </div>
        <div class="search-item">
          <span class="search-label">权限字符</span>
          <v-text-field v-model="queryParams.perms" placeholder="请输入权限字符" variant="outlined" density="compact"
            hide-details rounded="xl" class="search-input" @keyup.enter="handleQuery" />
        </div>
        <div class="search-item">
          <span class="search-label">状态</span>
          <v-select v-model="queryParams.status" :items="statusOptions" placeholder="请选择" variant="outlined"
            density="compact" hide-details rounded="xl" class="search-input--select" clearable />
        </div>

        <div class="search-actions d-flex ga-3 ml-auto">
          <v-btn color="#2655f1" rounded="xl" @click="handleQuery" class="action-btn">
            <v-icon start icon="mdi-magnify" />查询
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" @click="resetQuery" class="action-btn">
            <v-icon start icon="mdi-refresh" />重置
          </v-btn>
          <v-btn v-hasPermi="['system:menu:add']" variant="outlined" color="#2655f1" rounded="xl"
            @click="handleAddHeader" class="action-btn">
            <v-icon start icon="mdi-plus" />新增菜单
          </v-btn>
        </div>
      </div>
    </v-card>

    <!-- 表格卡片 -->
    <v-card class="rounded-xl card-bg card-bd card-table flex-grow-1 d-flex flex-column overflow-hidden mt-3"
      variant="elevated" elevation="0" :loading="loading">
      <div class="table-wrapper flex-grow-1 overflow-hidden d-flex flex-column">
        <v-table fixed-header height="100%" style="width: 100%; background: transparent" class="gm-table border-none">
          <thead>
            <tr>
              <th width="340">菜单名称</th>
              <th width="220">权限字符</th>
              <th width="250">组件路径</th>
              <th width="220">状态</th>
              <th width="300">创建时间</th>
              <th width="220" class="text-center">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in visibleRows" :key="row.menuId">
              <td>
                <div class="menu-name-cell">
                  <div class="menu-name-left" :style="{ paddingLeft: row.level * 24 + 'px' }">
                    <v-btn v-if="row.hasChildren" variant="text" density="compact" icon="mdi-chevron-right"
                      class="menu-expand-btn" :class="{ expanded: row.isExpanded }"
                      @click="toggleExpanded(row.menuId)" />
                    <div v-else class="menu-expand-placeholder" />

                    <span class="menu-name-text">
                      {{ row.menuName || "--" }}
                    </span>
                  </div>
                  <div class="menu-badge" v-if="row.menuType">
                    <v-chip size="x-small" variant="tonal" :color="getMenuTypeColor(row.menuType)"
                      class="menu-badge-chip">
                      {{ menuTypeLabel(row.menuType) }}
                    </v-chip>
                  </div>
                </div>
              </td>
              <td>
                <span class="text-truncate text-medium-emphasis">{{ row.perms || "--" }}</span>
              </td>
              <td>
                <span class="text-truncate text-medium-emphasis">{{ row.component || "--" }}</span>
              </td>
              <td>
                <v-chip :color="row.status === '0' || row.status === 0 ? '#33BE36' : '#FF6A46'" variant="tonal"
                  class="status-chip">
                  {{ row.status === "0" || row.status === 0 ? "正常" : "停用" }}
                </v-chip>
              </td>
              <td class="text-medium-emphasis">
                {{ row.createTime ? formatDateCell(row.createTime) : "--" }}
              </td>
              <td>
                <div class="menu-actions justify-start">
                  <v-btn v-hasPermi="['system:menu:add']" variant="text" color="#2655f1" rounded="xl"
                    @click="handleAddFromRow(row)">
                    新增
                  </v-btn>

                  <template v-if="!isFixedTopLevel(row)">
                    <v-btn v-hasPermi="['system:menu:edit']" variant="text" color="#2655f1" rounded="xl"
                      @click="handleEdit(row)">
                      修改
                    </v-btn>

                    <v-btn v-hasPermi="['system:menu:remove']" variant="text" color="#FF6A46" rounded="xl"
                      @click="handleDelete(row)">
                      删除
                    </v-btn>
                  </template>
                </div>
              </td>
            </tr>

            <tr v-if="visibleRows.length === 0">
              <td colspan="6" class="text-center py-12 text-medium-emphasis">
                <v-icon size="48" color="grey-lighten-2" class="mb-2">mdi-database-off</v-icon>
                <div class="text-body-1">暂无菜单数据</div>
              </td>
            </tr>
          </tbody>
        </v-table>
      </div>
    </v-card>

    <!-- 删除确认弹窗 -->
    <v-dialog v-model="deleteDialogVisible" max-width="420px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #3a68ff">删除</span><span>确认</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="deleteDialogVisible = false" />
        </div>
        <v-card-text class="pa-8 pt-2 pb-4">
          <div class="text-body-1">是否确认删除「{{ deleteTargetName }}」？</div>
        </v-card-text>
        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4"
            @click="deleteDialogVisible = false">取消</v-btn>
          <v-btn color="#3a68ff" variant="flat" class="gm-btn-primary px-10 text-white" :loading="deleteLoading"
            @click="doDelete">确定</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 新增/编辑菜单弹窗 -->
    <v-dialog v-model="dialogVisible" max-width="740px" persistent class="gm-dialog">
      <v-card class="rounded-30 overflow-hidden dialog-card">
        <div class="gm-dialog-header d-flex align-center px-8 py-6">
          <div class="text-h5">
            <span style="color: #2655F1;">{{ dialogTitle.substring(0, 2) }}</span>
            <span>{{ dialogTitle.substring(2) }}</span>
          </div>
          <v-spacer />
          <v-btn icon="mdi-close" variant="text" color="rgba(46, 46, 46, 0.5)" @click="dialogVisible = false" />
        </div>

        <v-card-text class="pa-8 pt-0">
          <v-form>
            <div class="form-grid-2">
              <div class="form-group grid-full">
                <label>上一级菜单 <span class="required-star">*</span></label>
                <el-tree-select v-model="form.parentId" :data="menuOptions" :teleported="false"
                  :props="{ label: 'label', children: 'children', value: 'id' }" :disabled="dialogTitle === '修改菜单'"
                  placeholder="请选择上级菜单" check-strictly class="gm-tree-select" filterable clearable default-expand-all />
              </div>

              <div class="form-group grid-full">
                <label>菜单类型</label>
                <v-radio-group v-model="form.menuType" :disabled="dialogTitle === '修改菜单'" inline hide-details
                  class="gm-radio-group">
                  <div class="d-flex ga-4 w-100">
                    <v-radio label="目录(M)" value="M" class="gm-radio-card flex-1" color="#2655F1" />
                    <v-radio label="按钮/接口(F)" value="F" class="gm-radio-card flex-1" color="#2655F1" />
                  </div>
                </v-radio-group>
              </div>

              <div class="form-group">
                <label>{{ form.menuType === "F" ? "权限名称" : "菜单名称" }} <span class="required-star">*</span></label>
                <v-text-field v-model="form.menuName" :placeholder="form.menuType === 'F' ? '请输入权限名称' : '请输入菜单名称'"
                  variant="solo" density="compact" class="gm-input" hide-details />
              </div>

              <div class="form-group">
                <label>显示排序</label>
                <v-text-field v-model.number="form.orderNum" type="number" placeholder="请输入排序" variant="solo"
                  density="compact" class="gm-input" hide-details />
              </div>

              <div v-if="form.menuType !== 'F'" class="form-group">
                <label>路由地址</label>
                <v-text-field v-model="form.path" placeholder="请输入路由地址" variant="solo" density="compact"
                  class="gm-input" hide-details />
              </div>

              <div v-if="form.menuType === 'C'" class="form-group">
                <label>组件路径</label>
                <v-text-field v-model="form.component" placeholder="请输入组件路径" variant="solo" density="compact"
                  class="gm-input" hide-details />
              </div>

              <div v-if="form.menuType === 'C'" class="form-group">
                <label>路由参数</label>
                <v-text-field v-model="form.query" placeholder="请输入路由参数" variant="solo" density="compact"
                  class="gm-input" hide-details maxlength="255" />
              </div>

              <div v-if="form.menuType === 'C' || form.menuType === 'F'" class="form-group">
                <label>权限字符</label>
                <v-text-field v-model="form.perms" placeholder="请输入权限字符" variant="solo" density="compact"
                  class="gm-input" hide-details />
              </div>

              <!-- 状态布局合并 -->
              <div class="form-group grid-full">
                <div class="form-grid-2">
                  <div class="form-group">
                    <label>菜单状态</label>
                    <v-radio-group v-model="form.status" inline hide-details class="gm-radio-group">
                      <div class="d-flex ga-6 w-100">
                        <v-radio label="正常" value="0" class="gm-radio-card flex-1" color="#2655F1" />
                        <v-radio label="停用" value="1" class="gm-radio-card flex-1" color="#2655F1" />
                      </div>
                    </v-radio-group>
                  </div>
                  <div class="form-group" v-if="form.menuType !== 'F'">
                    <label>显示状态</label>
                    <v-radio-group v-model="form.visible" inline hide-details class="gm-radio-group">
                      <div class="d-flex ga-6 w-100">
                        <!-- 与 ecenter / RuoYi 一致：0 显示，1 隐藏 -->
                        <v-radio label="显示" value="0" class="gm-radio-card flex-1" color="#2655F1" />
                        <v-radio label="隐藏" value="1" class="gm-radio-card flex-1" color="#2655F1" />
                      </div>
                    </v-radio-group>
                  </div>
                </div>
              </div>
            </div>
          </v-form>
        </v-card-text>

        <v-card-actions class="px-8 pb-8 pt-0 justify-end">
          <v-btn variant="flat" color="#D9D9D9" class="gm-btn-secondary px-10 mr-4" @click="handleDialogCancel">
            取消
          </v-btn>
          <v-btn color="#2655f1" variant="flat" class="gm-btn-primary px-10 text-white" :loading="submitLoading"
            @click="submitForm">
            保存
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, reactive, ref } from "vue";
import { eui } from "ems-ui";
import {
  addMenuEcenter,
  delMenuEcenter,
  getMenuEcenter,
  listMenuEcenter,
  menuTreeselectEcenter,
  updateMenuEcenter,
} from "@/api/ecenter";
import { handleTree } from "@/utils/ruoyi";
import { formatDate } from "@/utils";

const systemSign = "gemini3";

type MenuRow = {
  menuId: number | string;
  parentId?: number | string;
  menuName?: string;
  icon?: string;
  orderNum?: number;
  perms?: string;
  component?: string;
  visible?: string | number;
  status?: string | number;
  createTime?: string;
  menuType?: string;
  children?: MenuRow[];
};

type VisibleRow = MenuRow & {
  level: number;
  hasChildren: boolean;
  isExpanded: boolean;
};

const fixedTopLevelMenuNames = [
  "工作台",
  "我的电站",
  "实时监控",
  "运营管理",
  "运维管理",
  "系统管理",
];

const menuRoots = ref<MenuRow[]>([]);
const menuFlat = ref<MenuRow[]>([]);

// 搜索参数
const queryParams = reactive<{ menuName: string | undefined; perms: string | undefined; status: string | undefined }>({
  menuName: undefined,
  perms: undefined,
  status: undefined,
});

const statusOptions = [
  { title: '正常', value: '0' },
  { title: '停用', value: '1' }
];

// 计算过滤后的树根节点
const filteredMenuRoots = computed(() => {
  const nameQuery = queryParams.menuName?.toLowerCase();
  const permsQuery = queryParams.perms?.toLowerCase();
  const statusQuery = queryParams.status;

  const hasSearch = !!(nameQuery || permsQuery || statusQuery !== undefined);
  if (!hasSearch) return menuRoots.value;

  const filterNodes = (nodes: MenuRow[]): MenuRow[] => {
    return nodes
      .map(node => ({ ...node }))
      .filter(node => {
        const matchesName = !nameQuery || node.menuName?.toLowerCase().includes(nameQuery);
        const matchesPerms = !permsQuery || node.perms?.toLowerCase().includes(permsQuery);
        const matchesStatus = statusQuery === undefined || statusQuery === null || String(node.status) === String(statusQuery);

        const selfMatches = matchesName && matchesPerms && matchesStatus;

        if (node.children && node.children.length > 0) {
          const filteredChildren = filterNodes(node.children);
          node.children = filteredChildren;
          return selfMatches || filteredChildren.length > 0;
        }

        return selfMatches;
      });
  };

  return filterNodes(menuRoots.value);
});

// 展开态
const expandedIdList = ref<Array<string | number>>([]);

const visibleRows = computed<VisibleRow[]>(() => {
  const expandedSet = new Set(expandedIdList.value);
  const out: VisibleRow[] = [];

  const walk = (nodes: MenuRow[], level: number) => {
    for (const node of nodes) {
      const id = node.menuId;
      const hasChildren = Boolean(node.children && node.children.length);
      const isExpanded = expandedSet.has(id);

      out.push({
        ...(node as any),
        level,
        hasChildren,
        isExpanded,
      });

      if (hasChildren && isExpanded) {
        walk(node.children || [], level + 1);
      }
    }
  };

  walk(filteredMenuRoots.value, 0);
  return out;
});

const menuOptions = ref<any[]>([]);

async function fetchMenuOptions() {
  const res = await menuTreeselectEcenter({ systemSign });
  const rawData = res?.data ?? res ?? [];
  menuOptions.value = [{ id: 0, label: "主类目", children: rawData }];
}

const levelById = ref<Map<string | number, number>>(new Map());

function computeLevels() {
  const map = new Map<string | number, number>();
  const walk = (nodes: MenuRow[], level: number) => {
    for (const node of nodes) {
      map.set(node.menuId, level);
      if (node.children && node.children.length) {
        walk(node.children, level + 1);
      }
    }
  };
  walk(menuRoots.value, 0);
  levelById.value = map;
}

function handleQuery() {
  const hasSearch = !!(queryParams.menuName || queryParams.perms || queryParams.status !== undefined);
  if (hasSearch) {
    autoExpandAll(filteredMenuRoots.value);
  }
}

function autoExpandAll(nodes: MenuRow[]) {
  const ids: Array<string | number> = [...expandedIdList.value];
  const collect = (list: MenuRow[]) => {
    list.forEach(n => {
      if (n.children && n.children.length > 0) {
        if (!ids.includes(n.menuId)) ids.push(n.menuId);
        collect(n.children);
      }
    });
  };
  collect(nodes);
  expandedIdList.value = ids;
}

function resetQuery() {
  queryParams.menuName = undefined;
  queryParams.perms = undefined;
  queryParams.status = undefined;
}

function isFixedTopLevel(row: MenuRow) {
  const parentId = row.parentId ?? 0;
  const isRoot = parentId === 0 || parentId === "0" || parentId == null;
  return isRoot && fixedTopLevelMenuNames.includes(row.menuName || "");
}

function menuTypeLabel(type?: string) {
  if (type === "M") return "目录";
  if (type === "C") return "菜单";
  if (type === "F") return "权限";
  return type || "--";
}

function getMenuTypeColor(type?: string) {
  if (type === "M") return "#2655f1";
  if (type === "C") return "#33BE36";
  if (type === "F") return "#6e7185";
  return "grey";
}

function formatDateCell(value: any) {
  try {
    return formatDate(value);
  } catch (e) {
    return "--";
  }
}

function toggleExpanded(id: string | number) {
  const idx = expandedIdList.value.findIndex((x) => String(x) === String(id));
  if (idx >= 0) {
    expandedIdList.value.splice(idx, 1);
  } else {
    expandedIdList.value.push(id);
  }
}

function ensureDefaultExpanded() {
  const ids: Array<string | number> = [];
  for (const root of menuRoots.value) {
    if (root.children && root.children.length) ids.push(root.menuId);
  }
  expandedIdList.value = ids;
}

function extractMenuArray(res: any): any[] {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.rows)) return res.rows;
  if (Array.isArray(res?.data?.rows)) return res.data.rows;
  if (Array.isArray(res?.data?.list)) return res.data.list;
  return [];
}

function assignParentIds(nodes: MenuRow[], parentId: string | number = 0) {
  if (!Array.isArray(nodes)) return nodes;
  nodes.forEach((node) => {
    node.parentId = parentId;
    if (Array.isArray(node.children) && node.children.length) {
      assignParentIds(node.children, node.menuId);
    }
  });
  return nodes;
}

function flattenTree(nodes: MenuRow[], out: MenuRow[] = []): MenuRow[] {
  if (!Array.isArray(nodes)) return out;
  nodes.forEach((node) => {
    out.push(node);
    if (Array.isArray(node.children) && node.children.length) {
      flattenTree(node.children, out);
    }
  });
  return out;
}

const loading = ref(false);
async function fetchMenu() {
  loading.value = true;
  try {
    const res = await listMenuEcenter({ systemSign });
    const rawArray = extractMenuArray(res);
    const rows: MenuRow[] = Array.isArray(rawArray) ? rawArray : [];

    if (rows.length === 0) {
      menuRoots.value = [];
      menuFlat.value = [];
      levelById.value = new Map();
      return;
    }

    const hasAnyParentId = rows.some((it) => it?.parentId !== undefined && it?.parentId !== null);
    const roots = hasAnyParentId
      ? (handleTree(rows, "menuId", "parentId", "children") as MenuRow[])
      : (rows as MenuRow[]);

    assignParentIds(roots, 0);

    menuRoots.value = roots;
    menuFlat.value = flattenTree(roots);
    computeLevels();

    if (expandedIdList.value.length === 0) {
      await nextTick();
      ensureDefaultExpanded();
    }
  } catch (error) {

  } finally {
    loading.value = false;
  }

}

const dialogVisible = ref(false);
const dialogTitle = ref<"新增菜单" | "修改菜单">("新增菜单");
const submitLoading = ref(false);

const defaultForm = () => ({
  menuId: undefined,
  parentId: 0,
  menuName: "",
  icon: "",
  menuType: "M",
  orderNum: 1,
  isFrame: "1",
  isCache: "0",
  visible: "0",
  status: "0",
  path: "",
  component: "",
  query: "",
  perms: "",
});

const form = reactive<any>(defaultForm());
const dialogFormMode = ref<"add" | "edit">("add");

function resetForm() {
  const next = defaultForm();
  Object.keys(form).forEach((k) => delete form[k]);
  Object.assign(form, next);
}

function applyMenuTypeDefault(parent: MenuRow | undefined) {
  const pt = parent?.menuType;
  if (pt === "M") form.menuType = "C";
  else if (pt === "C") form.menuType = "F";
  else form.menuType = "M";
}

function handleAddHeader() {
  resetForm();
  dialogTitle.value = "新增菜单";
  dialogFormMode.value = "add";
  fetchMenuOptions();
  dialogVisible.value = true;
}

function handleAddFromRow(row: MenuRow) {
  resetForm();
  dialogTitle.value = "新增菜单";
  dialogFormMode.value = "add";
  form.parentId = row.menuId;
  applyMenuTypeDefault(row);
  fetchMenuOptions();
  dialogVisible.value = true;
}

async function handleEdit(row: MenuRow) {
  if (!row.menuId) return;
  resetForm();
  dialogTitle.value = "修改菜单";
  dialogFormMode.value = "edit";

  await fetchMenuOptions();
  const res = await getMenuEcenter(row.menuId);
  const data = (res?.data ?? res) as any;

  Object.assign(form, data);
  dialogVisible.value = true;
}

function handleDialogCancel() {
  dialogVisible.value = false;
  submitLoading.value = false;
}

async function submitForm() {
  try {
    if (form.parentId === undefined || form.parentId === null || form.parentId === "") {
      eui.showToast("请选择上级菜单", "warning");
      return;
    }
    if (!form.menuName) {
      eui.showToast(form.menuType === "F" ? "请输入权限名称" : "请输入菜单名称", "warning");
      return;
    }

    submitLoading.value = true;
    const payload = {
      ...form,
      systemSign,
      orderNum: form.orderNum === "" ? undefined : Number(form.orderNum),
    };

    if (dialogFormMode.value === "edit") {
      await updateMenuEcenter(payload);
      eui.showToast("修改成功", "success");
    } else {
      await addMenuEcenter(payload);
      eui.showToast("新增成功", "success");
    }

    dialogVisible.value = false;
    await fetchMenu();
  } catch (e) {
    console.error(e);
  } finally {
    submitLoading.value = false;
  }
}

const deleteDialogVisible = ref(false);
const deleteLoading = ref(false);
const deleteTargetRow = ref<MenuRow | null>(null);
const deleteTargetName = computed(() => deleteTargetRow.value?.menuName || "");

function handleDelete(row: MenuRow) {
  if (isFixedTopLevel(row)) {
    eui.showToast("该一级菜单为只读项，无法删除", "warning");
    return;
  }
  deleteTargetRow.value = row;
  deleteDialogVisible.value = true;
}

async function doDelete() {
  if (!deleteTargetRow.value) return;
  deleteLoading.value = true;
  try {
    await delMenuEcenter(deleteTargetRow.value.menuId);
    eui.showToast("删除成功", "success");
    deleteDialogVisible.value = false;
    await fetchMenu();
  } finally {
    deleteLoading.value = false;
  }
}

onMounted(async () => {
  await fetchMenu();
});
</script>

<style scoped lang="scss">
.card-bg {
  background: #FFF;
  backdrop-filter: blur(8px);
}

.card-bd {
  border-radius: 0 0 30px 30px !important;
}

.card-table {
  padding: 24px 16px;
  border-radius: 30px !important;
}

.search-item {
  display: flex;
  align-items: center;
  gap: 12px;

  .search-label {
    white-space: nowrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.7);
  }

  .search-input {
    width: 220px;

    // :deep(.v-field__outline) {
    //   border: 1px solid #d4d9e5 !important;
    //   color: transparent !important;
    // }
  }

  .search-input--select {
    width: 220px;

    // :deep(.v-field__outline) {
    //   border: 1px solid #d4d9e5 !important;
    //   color: transparent !important;
    // }
  }
}

.action-btn {
  min-width: 100px;
  height: 40px !important;
}

.gm-table {
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100%;

  :deep(.v-table__wrapper) {
    flex: 1;
    overflow-y: auto !important;
  }

  :deep(thead) {
    tr {
      background: #f8fafc !important;
    }

    th {
      font-weight: 600 !important;
      color: rgba(0, 0, 0, 0.8) !important;
      border-bottom: none !important;
      background: #f8fafc !important;
      position: sticky !important;
      top: 0 !important;
      z-index: 10 !important;
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }

  :deep(tbody) {
    tr:hover {
      background: rgba(38, 85, 241, 0.04) !important;
    }

    td {
      border-bottom: 1px solid rgba(0, 0, 0, 0.04) !important;
      height: 56px !important;
      padding-left: 24px !important;
      padding-right: 24px !important;
    }
  }
}

.menu-name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.menu-name-left {
  display: flex;
  align-items: center;
  min-width: 0;
}

.menu-expand-btn {
  transition: transform 0.2s;
  // width: 24px;
  // min-width: 24px;
  // height: 24px;
  color: #2655f1;
}

.menu-expand-btn.expanded {
  transform: rotate(90deg);
}

.menu-expand-placeholder {
  width: 24px;
  min-width: 24px;
}

.menu-name-text {
  display: inline-block;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
  color: #2655f1;
  padding-left: 8px;
}

.menu-actions {
  display: flex;
  gap: 4px;
}

.menu-badge-chip {
  height: 18px;
  font-size: 10px;
  padding: 0 6px;
}

.status-chip {
  // font-weight: 600;
  min-width: 60px;
  height: 28px !important;
  line-height: 28px !important;
  justify-content: center;
  border: 1px solid currentColor !important;
}

.form-grid-2 {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    font-size: 14px;
    font-weight: 500;
    color: #2E2E2E;
  }

  &.grid-full {
    grid-column: span 2;
  }
}


// 弹窗样式重写
.gm-dialog {
  :deep(.v-overlay__content) {
    border-radius: 30px !important;
  }
}

.dialog-card {
  border-radius: 30px !important;
  background: #FFF;
}

.gm-dialog-header {
  background: linear-gradient(185deg, #2655f160 0%, #fff0 60%);
  position: relative;

  .text-h5 {
    font-size: 24px !important;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
}


.gm-input {
  :deep(.v-field) {
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
    box-shadow: none !important;
    padding-inline: 4px !important;
    // border: 1px solid rgba(0, 0, 0, 0.05) !important;

    .v-field__outline {
      display: none !important;
    }

    input,
    .v-select__selection {
      font-size: 14px;
      color: rgba(46, 46, 46, 0.8);
      font-weight: 400;
    }
  }

  // 获得焦点时增加蓝色边框
  &.v-input--focused :deep(.v-field) {
    border-color: #2655f1 !important;
    // background-color: #FFF !important; // 移除聚焦变白逻辑
  }
}

.gm-radio-group {
  :deep(.v-selection-control-group) {
    width: 100%;
  }
}

.gm-radio-card {
  flex: 1;
  height: 42px;
  background-color: rgba(239, 244, 251, 0.7) !important;
  // border: 1px solid #D4D9E5 !important;
  border-radius: 15px !important;
  transition: all 0.2s ease !important;
  cursor: pointer;
  display: flex !important;
  align-items: center;
  justify-content: center;

  &:hover {
    border-color: #2655f1 !important;
  }

  :deep(.v-selection-control) {
    width: 100%;
    height: 100%;
    margin: 0 !important;
    padding: 0 !important;

    .v-selection-control__wrapper {
      display: none;
    }
  }

  :deep(.v-label) {
    font-size: 14px !important;
    color: #2E2E2E;
  }

  // 选中态显示
  &.v-selection-control--active {
    border-color: #2655f1 !important;
    background-color: #2655f1 !important;
    box-shadow: 0 4px 12px rgba(38, 85, 241, 0.2) !important;

    :deep(.v-label) {
      color: #FFF !important;
      font-weight: 600 !important;
    }
  }
}

.gm-btn-secondary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  color: #545454 !important;
  box-shadow: none !important;
}

.gm-btn-primary {
  height: 42px !important;
  border-radius: 12px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  box-shadow: none !important;
}

.gm-tree-select {
  width: 100%;

  :deep(.el-select__wrapper) {
    background-color: rgba(239, 244, 251, 0.7) !important;
    border-radius: 15px !important;
    box-shadow: none !important;
    border: 1px solid rgba(0, 0, 0, 0.05) !important;
    height: 40px !important;
    padding: 0 12px !important;

    &.is-focused {
      border-color: #2655f1 !important;
      background-color: #FFF !important;
    }
  }

  :deep(.el-select__placeholder) {
    color: rgba(46, 46, 46, 0.5);
    font-size: 14px;
  }

  :deep(.el-select__selection) {
    font-size: 14px;
    color: rgba(46, 46, 46, 0.8);
  }
}

.required-star {
  color: #ff4d4f;
}
</style>
